import { useRef, useCallback, useEffect } from 'react';
import SimplePeer from 'simple-peer';
import { useCall } from './CallProvider';

// STUN alone only works when both sides can connect directly -- it fails on
// many mobile data connections and restrictive WiFi/corporate networks, which
// was the actual cause of calls "not connecting". TURN below is a relay for
// exactly those cases; coturn is already installed and running on the VPS
// (vtdastrology.com), this was just never wired into the call code before.
// TURNS (TLS, port 5349) is included too since some networks block plain UDP
// outright but allow TLS-looking traffic through.
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'turn:vtdastrology.com:3478?transport=udp', username: 'astrotilak', credential: 'a3f326a602094505475978cc64baa481fb21fa34e240fa59' },
  { urls: 'turn:vtdastrology.com:3478?transport=tcp', username: 'astrotilak', credential: 'a3f326a602094505475978cc64baa481fb21fa34e240fa59' },
  { urls: 'turns:vtdastrology.com:5349?transport=tcp', username: 'astrotilak', credential: 'a3f326a602094505475978cc64baa481fb21fa34e240fa59' },
];

export default function useWebRTC({ onRemoteStream }) {
  const { callState, callInfo, localStream, remoteStream, socket, CALL_STATES } = useCall();
  const peerRef = useRef(null);

  const destroyPeer = useCallback(() => {
    if (peerRef.current) {
      try { peerRef.current.destroy(); } catch (_) {}
      peerRef.current = null;
    }
  }, []);

  const createPeer = useCallback((initiator) => {
    if (!localStream.current || !socket.current) return;

    destroyPeer();

    const peer = new SimplePeer({
      initiator,
      stream: localStream.current,
      trickle: true,
      config: { iceServers: ICE_SERVERS },
    });

    peer.on('signal', (data) => {
      socket.current?.emit('webrtc:signal', {
        to: callInfo?.to || callInfo?.from,
        type: data.type,
        payload: data,
        callType: callInfo?.callType || 'audio',
      });
    });

    peer.on('stream', (stream) => {
      remoteStream.current = stream;
      onRemoteStream?.(stream);
    });

    peer.on('error', (err) => {
      console.error('[WebRTC] Peer error:', err);
    });

    peer.on('close', () => {
      destroyPeer();
    });

    peerRef.current = peer;
  }, [callInfo, localStream, remoteStream, socket, destroyPeer, onRemoteStream]);

  // Handle incoming signals
  useEffect(() => {
    const handler = (data) => {
      if (peerRef.current && data?.payload) {
        try { peerRef.current.signal(data.payload); } catch (_) {}
      }
    };

    window._vtdWebRTCSignalHandler = handler;
    return () => { window._vtdWebRTCSignalHandler = null; };
  }, []);

  // Initiate or accept WebRTC connection based on call state changes
  useEffect(() => {
    if (callInfo?.method !== 'webrtc') return;

    if (callState === CALL_STATES.RINGING && callInfo?.direction === 'outgoing') {
      createPeer(true);
    } else if (callState === CALL_STATES.CONNECTING && callInfo?.direction === 'incoming') {
      createPeer(false);
    }
  }, [callState, callInfo, createPeer, CALL_STATES]);

  // Cleanup on call end
  useEffect(() => {
    if (callState === CALL_STATES.IDLE || callState === CALL_STATES.ENDED) {
      destroyPeer();
    }
  }, [callState, destroyPeer, CALL_STATES]);

  return { peer: peerRef };
}
